import { Client } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mail, Phone, MapPin, Calendar, Home as HomeIcon, Smartphone, Briefcase, User as UserIcon, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { InlineEditableField } from "@/components/InlineEditableField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

// Phone helpers (module-scope so both PersonalInfoPanel and subcomponents can use)
const splitPhoneAndExt = (raw: string): { base: string; ext?: string } => {
  const s = (raw || '').trim();
  const m = s.match(/^(.*?)(?:\s*(?:ext\.?|x)\s*(\d+))$/i);
  if (m) return { base: m[1] || '', ext: m[2] };
  return { base: s };
};

const formatDigitsToUS = (digits: string): string => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
};

const formatUSPhone = (val: string): string => {
  const { base, ext } = splitPhoneAndExt(val);
  const formatted = formatDigitsToUS(base);
  return ext ? `${formatted} x${ext}` : formatted;
};

const formatPhoneInput = (raw: string): string => {
  const { base, ext } = splitPhoneAndExt(raw);
  const formatted = formatDigitsToUS(base);
  return `${formatted}${ext ? ` x${ext}` : ''}`.trim();
};

const phoneTypeValues = ['Mobile', 'Work', 'Home'] as const;
type PhoneType = (typeof phoneTypeValues)[number];
const emailTypeValues = ['Personal', 'Work'] as const;
type EmailType = (typeof emailTypeValues)[number];

type ClientMethodDraft<TType extends string> = {
  id?: number;
  type: TType;
  value: string;
};

type PhoneRow = ClientMethodDraft<PhoneType> & { rowId: string };
type EmailRow = ClientMethodDraft<EmailType> & { rowId: string };

const isPhoneType = (value: string): value is PhoneType => value === 'Mobile' || value === 'Work' || value === 'Home';
const isEmailType = (value: string): value is EmailType => value === 'Work' || value === 'Personal';

const normalizePhoneLabel = (label: unknown): PhoneType => {
  if (typeof label !== 'string') {
    return 'Home';
  }

  const normalized = label.trim().toLowerCase();
  if (normalized === 'mobile') return 'Mobile';
  if (normalized === 'work') return 'Work';
  return 'Home';
};

const normalizeEmailLabel = (label: unknown): EmailType => (typeof label === 'string' && label.trim().toLowerCase() === 'work' ? 'Work' : 'Personal');

const generateRowId = (prefix: 'ph' | 'em') => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

type PersonalInfoPanelProps = {
  contact: Client;
  editMode?: boolean;
  onContactChange?: (updates: Partial<Client>) => void;
  onDraftChange?: (field: keyof Client, value: Client[keyof Client] | null) => void;
  onMethodsDraftChange?: (draft: {
    phones: ClientMethodDraft<PhoneType>[];
    emails: ClientMethodDraft<EmailType>[];
    primaryPhoneIndex: number;
    primaryEmailIndex: number;
  }) => void;
};

type ClientMethod = { id: number; clientId: number; kind: 'phone'|'email'; label: string | null; value: string | null; isPrimary: boolean; order: number };

export function PersonalInfoPanel({ contact, editMode = false, onContactChange, onDraftChange, onMethodsDraftChange }: PersonalInfoPanelProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [salutationEdit, setSalutationEdit] = useState(false);
  const [salutationDraft, setSalutationDraft] = useState<string | null>(contact.salutation);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const patchContact = useMutation({
    mutationFn: async (updates: Partial<Client>) => {
      const result = await apiRequest('PATCH', `/api/clients/${contact.id}`, updates);
      return result;
    },
    onSuccess: (data, variables) => {
      // Update caches and bubble up changes
      queryClient.invalidateQueries({ queryKey: ['/api/clients'] });
      onContactChange?.(variables);
      toast({ title: 'Saved', description: 'Client updated successfully.' });
    },
    onError: () => {
      toast({ title: 'Update failed', description: 'Could not save changes.', variant: 'destructive' });
    }
  });
  
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Derived multi-contact fields for edit-all
  const phoneTypes: PhoneType[] = [...phoneTypeValues];
  const emailTypes: EmailType[] = [...emailTypeValues];
  const [phoneRows, setPhoneRows] = useState<PhoneRow[]>([]);
  const [emailRows, setEmailRows] = useState<EmailRow[]>([]);
  const [primaryPhoneIndex, setPrimaryPhoneIndex] = useState<number>(0);
  const [primaryEmailIndex, setPrimaryEmailIndex] = useState<number>(0);

  const buildPhoneRowsFromContact = (c: Client): { type: PhoneType; value: string }[] => {
    const rows: { type: PhoneType; value: string }[] = [];
    if (c.phone) rows.push({ type: 'Home', value: c.phone });
    if (c.mobile) rows.push({ type: 'Mobile', value: c.mobile });
    if (c.workPhone) rows.push({ type: 'Work', value: c.workPhone });
    return rows.length ? rows : [{ type: 'Home', value: '' }];
  };

  const buildEmailRowsFromContact = (c: Client): { type: EmailType; value: string }[] => {
    const rows: { type: EmailType; value: string }[] = [];
    if (c.email) rows.push({ type: 'Personal', value: c.email });
    if (c.secondaryEmail) rows.push({ type: 'Work', value: c.secondaryEmail });
    return rows.length ? rows : [{ type: 'Personal', value: '' }];
  };

  // (helper functions moved to module scope)

  // Fetch contact methods (phones/emails)
  const { data: phoneMethods } = useQuery<ClientMethod[]>({
    queryKey: [`/api/clients/${contact.id}/contact-methods`, 'phone'],
    queryFn: async () => {
      const resp = await fetch(`/api/clients/${contact.id}/contact-methods?kind=phone`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      return resp.ok ? resp.json() : [];
    }
  });
  const { data: emailMethods } = useQuery<ClientMethod[]>({
    queryKey: [`/api/clients/${contact.id}/contact-methods`, 'email'],
    queryFn: async () => {
      const resp = await fetch(`/api/clients/${contact.id}/contact-methods?kind=email`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      return resp.ok ? resp.json() : [];
    }
  });

  // Update salutation draft when contact changes
  useEffect(() => {
    setSalutationDraft(contact.salutation);
  }, [contact.id, contact.salutation]);

  // Initialize rows when entering edit mode, preferring subresource if present
  useEffect(() => {
    if (editMode) {
      const havePhone = Array.isArray(phoneMethods) && phoneMethods.length > 0;
      if (havePhone) {
        const sorted = [...phoneMethods].sort((a,b)=> (b.isPrimary?1:0)-(a.isPrimary?1:0) || a.order - b.order);
        const ph: PhoneRow[] = sorted.map((m) => ({
          rowId: generateRowId('ph'),
          id: m.id,
          type: normalizePhoneLabel(m.label),
          value: m.value ?? '',
        }));
        setPhoneRows(ph);
        const pIdx = sorted.findIndex(m => m.isPrimary) ?? 0;
        setPrimaryPhoneIndex(pIdx >= 0 ? pIdx : 0);
      } else {
        const ph = buildPhoneRowsFromContact(contact).map((r) => ({
          rowId: generateRowId('ph'),
          id: undefined,
          ...r,
        }));
        setPhoneRows(ph);
        const pIdx = ph.findIndex(r => r.value === (contact.phone || ''));
        setPrimaryPhoneIndex(pIdx >= 0 ? pIdx : 0);
      }

      const haveEmail = Array.isArray(emailMethods) && emailMethods.length > 0;
      if (haveEmail) {
        const sorted = [...emailMethods].sort((a,b)=> (b.isPrimary?1:0)-(a.isPrimary?1:0) || a.order - b.order);
        const em: EmailRow[] = sorted.map((m) => ({
          rowId: generateRowId('em'),
          id: m.id,
          type: normalizeEmailLabel(m.label),
          value: m.value ?? '',
        }));
        setEmailRows(em);
        const eIdx = sorted.findIndex(m => m.isPrimary) ?? 0;
        setPrimaryEmailIndex(eIdx >= 0 ? eIdx : 0);
      } else {
        const em = buildEmailRowsFromContact(contact).map((r) => ({
          rowId: generateRowId('em'),
          id: undefined,
          ...r,
        }));
        setEmailRows(em);
        const eIdx = em.findIndex(r => r.value === (contact.email || ''));
        setPrimaryEmailIndex(eIdx >= 0 ? eIdx : 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, contact.id, Array.isArray(phoneMethods) ? phoneMethods.length : 0, Array.isArray(emailMethods) ? emailMethods.length : 0]);

  const mapPhoneRowsToDraft = (rows: PhoneRow[]): ClientMethodDraft<PhoneType>[] =>
    rows.map(({ id, type, value }) => ({
      id,
      type,
      value,
    }));

  const mapEmailRowsToDraft = (rows: EmailRow[]): ClientMethodDraft<EmailType>[] =>
    rows.map(({ id, type, value }) => ({
      id,
      type,
      value,
    }));

  const emitPhoneDraft = (rows: PhoneRow[], primaryIdx: number) => {
    if (rows.length === 0) {
      onDraftChange?.('phone', null);
      onDraftChange?.('mobile', null);
      onDraftChange?.('workPhone', null);
      onMethodsDraftChange?.({
        phones: [],
        emails: mapEmailRowsToDraft(emailRows),
        primaryPhoneIndex: 0,
        primaryEmailIndex,
      });
      return;
    }

    const safeIndex = Math.min(Math.max(primaryIdx, 0), rows.length - 1);
    const trimmedRows = rows.map((row) => ({ ...row, value: row.value.trim() }));

    const primaryValue = trimmedRows[safeIndex]?.value ?? '';
    const mobileValue = trimmedRows.find((row) => row.type === 'Mobile')?.value ?? '';
    const workValue = trimmedRows.find((row) => row.type === 'Work')?.value ?? '';

    onDraftChange?.('phone', primaryValue !== '' ? primaryValue : null);
    onDraftChange?.('mobile', mobileValue !== '' ? mobileValue : null);
    onDraftChange?.('workPhone', workValue !== '' ? workValue : null);

    onMethodsDraftChange?.({
      phones: mapPhoneRowsToDraft(rows),
      emails: mapEmailRowsToDraft(emailRows),
      primaryPhoneIndex: safeIndex,
      primaryEmailIndex,
    });
  };

  const emitEmailDraft = (rows: EmailRow[], primaryIdx: number) => {
    if (rows.length === 0) {
      onDraftChange?.('email', null);
      onDraftChange?.('secondaryEmail', null);
      onMethodsDraftChange?.({
        phones: mapPhoneRowsToDraft(phoneRows),
        emails: [],
        primaryPhoneIndex,
        primaryEmailIndex: 0,
      });
      return;
    }

    const safeIndex = Math.min(Math.max(primaryIdx, 0), rows.length - 1);
    const ordered = [rows[safeIndex], ...rows.filter((_, i) => i !== safeIndex)].filter(Boolean) as EmailRow[];
    const primaryValue = ordered[0]?.value.trim() ?? '';
    const secondaryValue = ordered.slice(1).find((row) => row)?.value.trim() ?? '';

    onDraftChange?.('email', primaryValue !== '' ? primaryValue : null);
    onDraftChange?.('secondaryEmail', secondaryValue !== '' ? secondaryValue : null);

    onMethodsDraftChange?.({
      phones: mapPhoneRowsToDraft(phoneRows),
      emails: mapEmailRowsToDraft(rows),
      primaryPhoneIndex,
      primaryEmailIndex: safeIndex,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header avatar removed; avatar shown in the main header above tabs */}

      <Card>
        <CardContent className="pt-6">
          <dl className="space-y-6">
            {/* Name fields - all on one row */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Name</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <div className="space-y-1">
                  <dt className="text-[12px] font-medium text-[rgb(120,113,108)]">Salutation</dt>
                  <dd className="text-sm text-gray-900">
                    {editMode ? (
                      <Select
                        value={contact.salutation ?? undefined}
                        onValueChange={(val)=>{
                          const next = val === 'none' ? null : val;
                          onDraftChange?.('salutation', next);
                        }}
                      >
                        <SelectTrigger className="h-9 w-full"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="Mr.">Mr.</SelectItem>
                          <SelectItem value="Ms.">Ms.</SelectItem>
                          <SelectItem value="Mrs.">Mrs.</SelectItem>
                          <SelectItem value="Dr.">Dr.</SelectItem>
                          <SelectItem value="Mx.">Mx.</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : salutationEdit ? (
                      <div className="flex items-center gap-1">
                        <Select
                          value={salutationDraft ?? undefined}
                          onValueChange={(val)=>{
                            setSalutationDraft(val === 'none' ? null : val);
                          }}
                        >
                          <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="Mr.">Mr.</SelectItem>
                            <SelectItem value="Ms.">Ms.</SelectItem>
                            <SelectItem value="Mrs.">Mrs.</SelectItem>
                            <SelectItem value="Dr.">Dr.</SelectItem>
                            <SelectItem value="Mx.">Mx.</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          onClick={async () => {
                            await patchContact.mutateAsync({ salutation: salutationDraft });
                            onContactChange?.({ salutation: salutationDraft });
                            setSalutationEdit(false);
                          }}
                        >
                          ✓
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => {
                            setSalutationDraft(contact.salutation);
                            setSalutationEdit(false);
                          }}
                        >
                          ✕
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="h-9 flex items-center px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => {
                          setSalutationEdit(true);
                          setSalutationDraft(contact.salutation);
                        }}
                      >
                        {contact.salutation || '-'}
                      </div>
                    )}
                  </dd>
                </div>
                <InlineEditableField
                  label="First"
                  value={contact.firstName}
                  placeholder="First Name"
                  forceEdit={false}
                  bulkEdit={editMode}
                  className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                  onChange={(val)=> onDraftChange?.('firstName', val)}
                  onSave={async (val)=> patchContact.mutateAsync({ firstName: val })}
                />
                <InlineEditableField
                  label="Middle"
                  value={contact.middleName}
                  placeholder="Middle"
                  forceEdit={false}
                  bulkEdit={editMode}
                  className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                  onChange={(val)=> onDraftChange?.('middleName', val)}
                  onSave={async (val)=> patchContact.mutateAsync({ middleName: val })}
                />
                <InlineEditableField
                  label="Last"
                  value={contact.lastName}
                  placeholder="Last Name"
                  forceEdit={false}
                  bulkEdit={editMode}
                  className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                  onChange={(val)=> onDraftChange?.('lastName', val)}
                  onSave={async (val)=> patchContact.mutateAsync({ lastName: val })}
                />
                <InlineEditableField
                  label="Suffix"
                  value={contact.suffix}
                  placeholder="Jr., Sr."
                  forceEdit={false}
                  bulkEdit={editMode}
                  className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                  onChange={(val)=> onDraftChange?.('suffix', val)}
                  onSave={async (val)=> patchContact.mutateAsync({ suffix: val })}
                />
                <InlineEditableField
                  label="Preferred"
                  value={contact.preferredName}
                  placeholder="Preferred"
                  forceEdit={false}
                  bulkEdit={editMode}
                  className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                  onChange={(val)=> onDraftChange?.('preferredName', val)}
                  onSave={async (val)=> patchContact.mutateAsync({ preferredName: val })}
                />
              </div>
            </div>
            
            {/* Personal details */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Personal Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-[12px] font-medium text-[rgb(120,113,108)] min-w-[80px]">Gender</span>
                  <Select
                    value={contact.gender ?? undefined}
                    onValueChange={(val)=>{
                      const next = val === 'unspecified' ? null : val;
                      onDraftChange?.('gender', next);
                      if (!editMode) patchContact.mutate({ gender: next });
                    }}
                  >
                    <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unspecified">Unspecified</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Non-binary">Non-binary</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[12px] font-medium text-[rgb(120,113,108)] min-w-[80px]">Date of Birth</span>
                  <Input
                    type="date"
                    className="h-9 w-[180px]"
                    value={contact.birthdate ? String(contact.birthdate).slice(0,10) : ''}
                    onChange={(e)=>{
                      onDraftChange?.('birthdate', e.target.value || null);
                      if (!editMode) patchContact.mutate({ birthdate: e.target.value || null });
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Contact Information - Phones and Emails */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Phones (Home/Mobile/Work) with type + primary + add */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Phone Numbers</h4>
                <div className="space-y-3">
                  {editMode ? (
                    <RadioGroup value={String(primaryPhoneIndex)} onValueChange={(v)=>{ const idx = parseInt(v); setPrimaryPhoneIndex(idx); emitPhoneDraft(phoneRows, idx); }}>
                      {phoneRows.map((row, idx) => (
                        <div key={row.rowId} className="flex items-center gap-2">
                          <Select
                            value={row.type}
                            onValueChange={(nextType) => {
                              if (!isPhoneType(nextType)) return;
                              setPhoneRows((prev) => {
                                const next = prev.map((r, i) => (i === idx ? { ...r, type: nextType } : r));
                                emitPhoneDraft(next, primaryPhoneIndex);
                                return next;
                              });
                            }}
                          >
                            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {phoneTypes.map(tt => (
                                <SelectItem key={tt} value={tt}>{tt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            className="h-9 flex-1"
                            value={formatPhoneInput(row.value)}
                            placeholder="(555) 123-4567"
                            onChange={(e)=>{
                              const val = formatPhoneInput(e.target.value);
                              setPhoneRows(prev => {
                                const next = prev.map((r,i)=> i===idx ? { ...r, value: val } : r);
                                emitPhoneDraft(next, primaryPhoneIndex);
                                return next;
                              });
                            }}
                          />
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value={String(idx)} id={`primary-phone-${row.rowId}`} />
                            <Label className="text-[12px] text-[rgb(120,113,108)]" htmlFor={`primary-phone-${row.rowId}`}>Primary</Label>
                            {idx !== primaryPhoneIndex && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" aria-label="Delete phone row">
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this phone number?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will remove the number from this contact. You can’t undo this action.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => {
                                        setPhoneRows((prev) => {
                                          const delIndex = idx;
                                          const next = prev.filter((_, j) => j !== delIndex);
                                          let newPrimary = primaryPhoneIndex;
                                          if (delIndex < primaryPhoneIndex) newPrimary = Math.max(0, primaryPhoneIndex - 1);
                                          if (newPrimary >= next.length) newPrimary = Math.max(0, next.length - 1);
                                          setPrimaryPhoneIndex(newPrimary);
                                          emitPhoneDraft(next, newPrimary);
                                          return next;
                                        });
                                      }}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      ))}
                      {phoneRows.length < 3 && (
                        <Button variant="outline" size="sm" onClick={() => {
                          setPhoneRows(prev => {
                            const newRow: PhoneRow = { rowId: generateRowId('ph'), id: undefined, type: 'Mobile', value: '' };
                            const next: PhoneRow[] = [...prev, newRow];
                            emitPhoneDraft(next, primaryPhoneIndex);
                            return next;
                          });
                        }}>
                          <Plus className="h-4 w-4 mr-1" /> Add phone
                        </Button>
                      )}
                    </RadioGroup>
                  ) : (
                    // View mode: use contact_methods when available, else fallback
                    Array.isArray(phoneMethods) && phoneMethods.length > 0 ? (
                      phoneMethods
                        .filter(m => (m.value || '').trim().length > 0)
                        .sort((a,b)=> (b.isPrimary?1:0)-(a.isPrimary?1:0) || a.order - b.order)
                        .map((m, idx) => (
                          <PhoneViewRow
                            key={`view-ph-${m.id}`}
                            type={normalizePhoneLabel(m.label)}
                            value={m.value || ''}
                            formatUSPhone={formatUSPhone}
                            onSave={async (newType, newVal) => {
                              const trimmed = (newVal || '').trim();
                              await fetch(`/api/client-methods/${m.id}`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                body: JSON.stringify({ label: newType, value: trimmed || null })
                              });
                              // Optimistically update caches for snappy UI
                              const updateCache = (kind: 'phone' | 'email') => {
                                const baseKey = [`/api/clients/${contact.id}/contact-methods`, kind] as const;
                                const keys: QueryKey[] = [
                                  baseKey,
                                  [...baseKey, 'header'] as const,
                                  [...baseKey, 'primary-cell'] as const,
                                ];
                                keys.forEach((key) => {
                                  queryClient.setQueryData<ClientMethod[]>(key, (oldData) => {
                                    if (!Array.isArray(oldData)) return oldData;
                                    return oldData.map((row) =>
                                      row.id === m.id ? { ...row, label: newType, value: trimmed || null } : row
                                    );
                                  });
                                });
                              };
                              updateCache('phone');
                              await queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/contact-methods`], exact: false });
                            }}
                          />
                        ))
                    ) : (
                      buildPhoneRowsFromContact(contact).map((row, idx) => (
                        row.value ? (
                          <PhoneViewRow
                            key={`view-ph-fallback-${idx}`}
                            type={row.type}
                            value={row.value}
                            formatUSPhone={formatUSPhone}
                            onSave={async (newType, newVal) => {
                              const oldField = row.type === 'Home' ? 'phone' : row.type === 'Mobile' ? 'mobile' : 'workPhone';
                              const newField = newType === 'Home' ? 'phone' : newType === 'Mobile' ? 'mobile' : 'workPhone';
                              const patch: Partial<Client> = {};
                              const trimmedValue = newVal.trim();

                              if (oldField !== newField) {
                                if (oldField === 'phone') patch.phone = null;
                                if (oldField === 'mobile') patch.mobile = null;
                                if (oldField === 'workPhone') patch.workPhone = null;
                              }

                              if (newField === 'phone') patch.phone = trimmedValue ? trimmedValue : null;
                              if (newField === 'mobile') patch.mobile = trimmedValue ? trimmedValue : null;
                              if (newField === 'workPhone') patch.workPhone = trimmedValue ? trimmedValue : null;
                              await patchContact.mutateAsync(patch);
                              onContactChange?.(patch);
                            }}
                          />
                        ) : null
                      ))
                    )
                  )}
                </div>
              </div>

              {/* Emails (Personal/Work) with type + primary + add */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Email Addresses</h4>
                <div className="space-y-3">
                  {editMode ? (
                    <RadioGroup value={String(primaryEmailIndex)} onValueChange={(v)=>{ const idx = parseInt(v); setPrimaryEmailIndex(idx); emitEmailDraft(emailRows, idx); }}>
                      {emailRows.map((row, idx) => (
                        <div key={row.rowId} className="flex items-center gap-2">
                          <Select
                            value={row.type}
                            onValueChange={(nextType) => {
                              if (!isEmailType(nextType)) return;
                              setEmailRows((prev) => {
                                const next = prev.map((r, i) => (i === idx ? { ...r, type: nextType } : r));
                                emitEmailDraft(next, primaryEmailIndex);
                                return next;
                              });
                            }}
                          >
                            <SelectTrigger className="w-28 h-8 focus:ring-blue-100 focus:border-blue-400"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {emailTypes.map(tt => (
                                <SelectItem key={tt} value={tt}>{tt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            className="h-9 flex-1"
                            type="email"
                            value={row.value}
                            placeholder="name@example.com"
                            onChange={(e)=>{
                              const val = e.target.value;
                              setEmailRows(prev => {
                                const next = prev.map((r,i)=> i===idx ? { ...r, value: val } : r);
                                emitEmailDraft(next, primaryEmailIndex);
                                return next;
                              });
                            }}
                          />
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value={String(idx)} id={`primary-email-${row.rowId}`} />
                            <Label className="text-[12px] text-[rgb(120,113,108)]" htmlFor={`primary-email-${row.rowId}`}>Primary</Label>
                            {idx !== primaryEmailIndex && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" aria-label="Delete email row">
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this email address?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will remove the email from this contact. You can’t undo this action.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => {
                                        setEmailRows((prev) => {
                                          const delIndex = idx;
                                          const next = prev.filter((_, j) => j !== delIndex);
                                          let newPrimary = primaryEmailIndex;
                                          if (delIndex < primaryEmailIndex) newPrimary = Math.max(0, primaryEmailIndex - 1);
                                          if (newPrimary >= next.length) newPrimary = Math.max(0, next.length - 1);
                                          setPrimaryEmailIndex(newPrimary);
                                          emitEmailDraft(next, newPrimary);
                                          return next;
                                        });
                                      }}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      ))}
                      {emailRows.length < 2 && (
                        <Button variant="outline" size="sm" onClick={() => {
                          setEmailRows(prev => {
                            const newRow: EmailRow = { rowId: generateRowId('em'), id: undefined, type: 'Personal', value: '' };
                            const next: EmailRow[] = [...prev, newRow];
                            emitEmailDraft(next, primaryEmailIndex);
                            return next;
                          });
                        }}>
                          <Plus className="h-4 w-4 mr-1" /> Add email
                        </Button>
                      )}
                    </RadioGroup>
                  ) : (
                    Array.isArray(emailMethods) && emailMethods.length > 0 ? (
                      emailMethods
                        .filter(m => (m.value || '').trim().length > 0)
                        .sort((a,b)=> (b.isPrimary?1:0)-(a.isPrimary?1:0) || a.order - b.order)
                        .map((m) => (
                          <EmailViewRow
                            key={`view-em-${m.id}`}
                            type={normalizeEmailLabel(m.label)}
                            value={m.value || ''}
                            onSave={async (newType, newVal) => {
                              const trimmed = (newVal || '').trim();
                              await fetch(`/api/client-methods/${m.id}`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                body: JSON.stringify({ label: newType, value: trimmed || null })
                              });
                              const updateCache = (kind: 'phone' | 'email') => {
                                const baseKey = [`/api/clients/${contact.id}/contact-methods`, kind] as const;
                                const keys: QueryKey[] = [
                                  baseKey,
                                  [...baseKey, 'header'] as const,
                                  [...baseKey, 'primary-cell'] as const,
                                ];
                                keys.forEach((key) => {
                                  queryClient.setQueryData<ClientMethod[]>(key, (oldData) => {
                                    if (!Array.isArray(oldData)) return oldData;
                                    return oldData.map((row) =>
                                      row.id === m.id ? { ...row, label: newType, value: trimmed || null } : row
                                    );
                                  });
                                });
                              };
                              updateCache('email');
                              await queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/contact-methods`], exact: false });
                            }}
                          />
                        ))
                    ) : (
                      buildEmailRowsFromContact(contact).map((row, idx) => (
                        row.value ? (
                          <EmailViewRow
                            key={`view-em-fallback-${idx}`}
                            type={row.type}
                            value={row.value}
                            onSave={async (newType, newVal) => {
                              const oldField = row.type === 'Personal' ? 'email' : 'secondaryEmail';
                              const newField = newType === 'Personal' ? 'email' : 'secondaryEmail';
                              const patch: Partial<Client> = {};
                              const trimmedValue = newVal.trim();
                              if (oldField !== newField) {
                                if (oldField === 'email') patch.email = null;
                                else patch.secondaryEmail = null;
                              }
                              if (newField === 'email') patch.email = trimmedValue ? trimmedValue : null;
                              else patch.secondaryEmail = trimmedValue ? trimmedValue : null;
                              await patchContact.mutateAsync(patch);
                              onContactChange?.(patch);
                            }}
                          />
                        ) : null
                      ))
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Mailing Address */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Mailing Address</h4>
                <div className="space-y-4">
                  <InlineEditableField
                    label="Street Address"
                    value={contact.address}
                    placeholder="123 Main St"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(val) => onDraftChange?.('address', val)}
                    onSave={async (val) => patchContact.mutateAsync({ address: val })}
                  />
                  
                  <div className="grid grid-cols-2 gap-3">
                    <InlineEditableField
                      label="City"
                      value={contact.city}
                      placeholder="City"
                      forceEdit={false}
                      bulkEdit={editMode}
                      className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                      onChange={(val) => onDraftChange?.('city', val)}
                      onSave={async (val) => patchContact.mutateAsync({ city: val })}
                    />
                    <InlineEditableField
                      label="State/Province"
                      value={contact.state}
                      placeholder="State/Province"
                      forceEdit={false}
                      bulkEdit={editMode}
                      className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                      onChange={(val) => onDraftChange?.('state', val)}
                      onSave={async (val) => patchContact.mutateAsync({ state: val })}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <InlineEditableField
                      label="Zip/Postal Code"
                      value={contact.zipCode}
                      placeholder="Postal Code"
                      forceEdit={false}
                      bulkEdit={editMode}
                      className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                      onChange={(val) => onDraftChange?.('zipCode', val)}
                      onSave={async (val) => patchContact.mutateAsync({ zipCode: val })}
                    />
                    <InlineEditableField
                      label="Country"
                      value={contact.country}
                      placeholder="Country"
                      forceEdit={false}
                      bulkEdit={editMode}
                      className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                      onChange={(val) => onDraftChange?.('country', val)}
                      onSave={async (val) => patchContact.mutateAsync({ country: val })}
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Billing Address</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Checkbox id="billingSame" checked={
                      (contact.billingAddress || contact.billingCity || contact.billingState || contact.billingZipCode || contact.billingCountry)
                        ? false : true
                    } onCheckedChange={(checked)=>{
                      const isChecked = !!checked;
                      if (isChecked) {
                        onDraftChange?.('billingAddress', null);
                        onDraftChange?.('billingCity', null);
                        onDraftChange?.('billingState', null);
                        onDraftChange?.('billingZipCode', null);
                        onDraftChange?.('billingCountry', null);
                      }
                    }} />
                    <Label htmlFor="billingSame" className="text-[12px] font-medium text-[rgb(120,113,108)]">Same as mailing address</Label>
                  </div>

                  {/* Billing fields (hidden if same as mailing) */}
                  { (contact.billingAddress || contact.billingCity || contact.billingState || contact.billingZipCode || contact.billingCountry) && (
                    <>
                      <InlineEditableField
                        label="Street Address"
                        value={contact.billingAddress}
                        placeholder="123 Main St"
                        forceEdit={false}
                        bulkEdit={editMode}
                        className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                        onChange={(val) => onDraftChange?.('billingAddress', val)}
                        onSave={async (val) => patchContact.mutateAsync({ billingAddress: val })}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <InlineEditableField
                          label="City"
                          value={contact.billingCity}
                          placeholder="City"
                          forceEdit={false}
                          bulkEdit={editMode}
                          className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                          onChange={(val) => onDraftChange?.('billingCity', val)}
                          onSave={async (val) => patchContact.mutateAsync({ billingCity: val })}
                        />
                        <InlineEditableField
                          label="State/Province"
                          value={contact.billingState}
                          placeholder="State/Province"
                          forceEdit={false}
                          bulkEdit={editMode}
                          className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                          onChange={(val) => onDraftChange?.('billingState', val)}
                          onSave={async (val) => patchContact.mutateAsync({ billingState: val })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <InlineEditableField
                          label="Zip/Postal Code"
                          value={contact.billingZipCode}
                          placeholder="Postal Code"
                          forceEdit={false}
                          bulkEdit={editMode}
                          className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                          onChange={(val) => onDraftChange?.('billingZipCode', val)}
                          onSave={async (val) => patchContact.mutateAsync({ billingZipCode: val })}
                        />
                        <InlineEditableField
                          label="Country"
                          value={contact.billingCountry}
                          placeholder="Country"
                          forceEdit={false}
                          bulkEdit={editMode}
                          className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                          onChange={(val) => onDraftChange?.('billingCountry', val)}
                          onSave={async (val) => patchContact.mutateAsync({ billingCountry: val })}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Social Media</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <InlineEditableField
                    label="Instagram"
                    value={contact.instagram}
                    placeholder="@handle"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(val) => onDraftChange?.('instagram', val)}
                    onSave={async (val) => patchContact.mutateAsync({ instagram: val })}
                  />
                  <InlineEditableField
                    label="Facebook"
                    value={contact.facebook}
                    placeholder="Facebook profile"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(val) => onDraftChange?.('facebook', val)}
                    onSave={async (val) => patchContact.mutateAsync({ facebook: val })}
                  />
                  <InlineEditableField
                    label="X (Twitter)"
                    value={contact.twitter}
                    placeholder="@handle"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(val) => onDraftChange?.('twitter', val)}
                    onSave={async (val) => patchContact.mutateAsync({ twitter: val })}
                  />
              </div>
            </div>

            {/* Emergency Contact */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Emergency Contact</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InlineEditableField
                    label="Contact Name"
                    value={contact.emergencyContactName}
                    placeholder="Name"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(v)=>onDraftChange?.('emergencyContactName', v)}
                    onSave={async (v)=> patchContact.mutateAsync({ emergencyContactName: v })}
                  />
                  <InlineEditableField
                    label="Relationship"
                    value={contact.emergencyContactRelationship}
                    placeholder="Relationship"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(v)=>onDraftChange?.('emergencyContactRelationship', v)}
                    onSave={async (v)=> patchContact.mutateAsync({ emergencyContactRelationship: v })}
                  />
                  <InlineEditableField
                    label="Contact Phone"
                    value={contact.emergencyContactPhone}
                    placeholder="(555) 123-4567"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(v)=>onDraftChange?.('emergencyContactPhone', v)}
                    onSave={async (v)=> patchContact.mutateAsync({ emergencyContactPhone: v })}
                  />
                  <InlineEditableField
                    label="Contact Email"
                    value={contact.emergencyContactEmail}
                    placeholder="name@example.com"
                    forceEdit={false}
                    bulkEdit={editMode}
                    className="[&>dt]:text-[12px] [&>dt]:font-medium [&>dt]:text-[rgb(120,113,108)]"
                    onChange={(v)=>onDraftChange?.('emergencyContactEmail', v)}
                    onSave={async (v)=> patchContact.mutateAsync({ emergencyContactEmail: v })}
                  />
              </div>
            </div>

            {/* Movement Restrictions */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Movement Restrictions</h4>
              <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Switch 
                      checked={!!contact.hasMovementRestrictions}
                      onCheckedChange={(checked)=>{
                        onDraftChange?.('hasMovementRestrictions', !!checked);
                        if (!editMode) patchContact.mutate({ hasMovementRestrictions: !!checked });
                      }} 
                    />
                    <Label className="text-[12px] font-medium text-[rgb(120,113,108)]">Has movement restrictions</Label>
                  </div>
                  {contact.hasMovementRestrictions && (
                    <div>
                      <Label className="text-[12px] font-medium text-[rgb(120,113,108)]">Restriction Notes</Label>
                      <Textarea
                        className="mt-1"
                        placeholder="Describe any movement restrictions"
                        value={contact.movementRestrictionsNotes || ''}
                        onChange={(e)=>{
                          onDraftChange?.('movementRestrictionsNotes', e.target.value);
                          if (!editMode) patchContact.mutate({ movementRestrictionsNotes: e.target.value });
                        }}
                      />
                    </div>
                  )}
              </div>
            </div>
          </dl>
        </CardContent>
      </Card>

      {contact.notes && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-medium mb-2">Notes</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{contact.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog - placeholder, retained for future multi-field form */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Personal Information</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Edit form will be implemented here. This will allow updating of name, contact details, address, etc.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// View-mode row with inline edit for Phones (type + value together)
function PhoneViewRow({ type, value, formatUSPhone, onSave }: {
  type: PhoneType;
  value: string;
  formatUSPhone: (v: string) => string;
  onSave: (newType: PhoneType, newVal: string) => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftType, setDraftType] = useState<PhoneType>(type);
  const [draftVal, setDraftVal] = useState<string>(value);
  const Icon = draftType === 'Work' ? Briefcase : draftType === 'Mobile' ? Smartphone : HomeIcon;

  const handleTypeChange = (next: string) => {
    if (isPhoneType(next)) {
      setDraftType(next);
    }
  };

  const handleSave = async () => {
    await onSave(draftType, draftVal);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraftType(type);
    setDraftVal(value);
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-2">
      {editing ? (
        <>
          <Select value={draftType} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Mobile">Mobile</SelectItem>
              <SelectItem value="Work">Work</SelectItem>
              <SelectItem value="Home">Home</SelectItem>
            </SelectContent>
          </Select>
          <Input
            className="h-9 flex-1"
            value={formatPhoneInput(draftVal)}
            onChange={(e) => setDraftVal(formatPhoneInput(e.target.value))}
            placeholder="(555) 123-4567"
          />
          <Button variant="ghost" size="icon" onClick={handleSave}>
            ✓
          </Button>
          <Button variant="ghost" size="icon" onClick={handleCancel}>
            ✕
          </Button>
        </>
      ) : (
        <>
          <span className="w-8 text-muted-foreground flex items-center justify-center"><Icon className="h-4 w-4" /></span>
          <span className="flex-1 cursor-pointer" onClick={() => setEditing(true)}>{formatUSPhone(value)}</span>
        </>
      )}
    </div>
  );
}


// View-mode row with inline edit for Emails (type + value together)
function EmailViewRow({ type, value, onSave }: {
  type: EmailType;
  value: string;
  onSave: (newType: EmailType, newVal: string) => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftType, setDraftType] = useState<EmailType>(type);
  const [draftVal, setDraftVal] = useState<string>(value);
  const Icon = draftType === 'Work' ? Briefcase : UserIcon;

  const handleTypeChange = (next: string) => {
    if (isEmailType(next)) {
      setDraftType(next);
    }
  };

  const handleSave = async () => {
    await onSave(draftType, draftVal);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraftType(type);
    setDraftVal(value);
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-2">
      {editing ? (
        <>
          <Select value={draftType} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Personal">Personal</SelectItem>
              <SelectItem value="Work">Work</SelectItem>
            </SelectContent>
          </Select>
          <Input
            className="h-9 flex-1"
            type="email"
            value={draftVal}
            onChange={(e) => setDraftVal(e.target.value)}
            placeholder="name@example.com"
          />
          <Button variant="ghost" size="icon" onClick={handleSave}>
            ✓
          </Button>
          <Button variant="ghost" size="icon" onClick={handleCancel}>
            ✕
          </Button>
        </>
      ) : (
        <>
          <span className="w-8 text-muted-foreground flex items-center justify-center"><Icon className="h-4 w-4" /></span>
          <span className="flex-1 cursor-pointer" onClick={() => setEditing(true)}>{value}</span>
        </>
      )}
    </div>
  );
}
